import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSites } from '@/hooks/useSites'
import MachineSelector from '@/components/machine/MachineSelector'
import ScenarioPanel from '@/components/scenario/ScenarioPanel'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import {
  AlertCircle,
  Forklift,
  PlayCircle,
  MapPin,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'

export default function HomePage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedSiteId = searchParams.get('site')
  const selectedMachineId = searchParams.get('machine')

  // 機体選択後はサイト・機体リストを畳む。トグルで再表示する。
  const [selectorOpen, setSelectorOpen] = useState(false)

  const handleSelectSite = useCallback(
    (siteId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (siteId) next.set('site', siteId)
          else next.delete('site')
          next.delete('machine')
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const handleSelectMachine = useCallback(
    (machineId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (machineId) next.set('machine', machineId)
          else next.delete('machine')
          return next
        },
        { replace: true }
      )
      // 機体を選んだらリストを畳む
      if (machineId) setSelectorOpen(false)
    },
    [setSearchParams]
  )

  const { data: sites, isLoading: sitesLoading, error: sitesError } = useSites()

  const selectedSite = sites?.find((s) => s.id === selectedSiteId)
  const selectedMachine = selectedSite?.robots.find(
    (m) => m.id === selectedMachineId
  )

  // 機体未選択時は常にリスト表示。選択済みなら畳んだ状態を既定にする。
  const showSelector = !selectedMachineId || selectorOpen

  return (
    <div className="h-full flex flex-col">
      {sitesLoading ? (
        <div className="flex-1 flex flex-col justify-center items-center gap-3">
          <LoadingSpinner size="lg" />
          <p className="text-sm text-gray-500">{t('machines.loading')}</p>
        </div>
      ) : sitesError ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {sitesError.message}
        </div>
      ) : sites && sites.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
          <Forklift className="w-12 h-12 mb-3 text-gray-300" />
          <p>{t('machines.noSites')}</p>
        </div>
      ) : sites ? (
        <div className="flex-1 min-h-0 bg-white border-t border-gray-200 flex flex-col overflow-hidden">
          {/* 機体選択済み: 畳み/展開バー（サイト・機体名 + トグル） */}
          {selectedMachineId && (
            <button
              onClick={() => setSelectorOpen((v) => !v)}
              className="flex items-center gap-2 px-4 py-1.5 border-b border-gray-200 bg-gray-50 text-sm hover:bg-gray-100 transition-colors"
            >
              <MapPin className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-700 font-medium">
                {selectedSite?.name}
              </span>
              <span className="text-gray-300">/</span>
              <Forklift className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-700 font-medium">
                {selectedMachine?.name ?? selectedMachineId}
              </span>
              <span className="ml-auto flex items-center gap-1 text-xs text-gray-500">
                {selectorOpen ? (
                  <>
                    <ChevronUp className="w-3.5 h-3.5" />
                    {t('machines.close')}
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3.5 h-3.5" />
                    {t('machines.change')}
                  </>
                )}
              </span>
            </button>
          )}

          <div className="flex-1 min-h-0 flex overflow-hidden">
            {showSelector && (
              <MachineSelector
                sites={sites}
                selectedSiteId={selectedSiteId}
                selectedMachineId={selectedMachineId}
                onSelectSite={handleSelectSite}
                onSelectMachine={handleSelectMachine}
              />
            )}

            {/* Scenario workspace */}
            <div className="flex-1 flex flex-col min-h-0 min-w-0">
              {!selectedSiteId || !selectedMachineId ? (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                  <PlayCircle className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-sm">{t('scenarios.selectMachine')}</p>
                </div>
              ) : (
                <ScenarioPanel
                  key={`${selectedSiteId}/${selectedMachineId}`}
                  siteId={selectedSiteId}
                  machineId={selectedMachineId}
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
